/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDEditor from "components/MDEditor";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

import cities from "data/cities.json";

// NewProduct page components
import FormField from "layouts/ecommerce/products/edit-product/components/FormField";
import EventCategories from "../../modals/Categories";
import EventPlaces from "../../modals/Places";
import EventSieges from "../../modals/Sieges";
import EventStands from "../../modals/Stands";
import EventSocial from "../../modals/Socials";

import { getAllCategoriesApi } from "api/categories";
import { getAllPlacesApi } from "api/places";
import { getAllStandsApi } from "api/stands";
import { getAllSiegesApi } from "api/sieges";
import { getAllSocialsApi } from "api/socials";

import Icon from "@mui/material/Icon";
import EventSelectedSieges from "../../modals/Sieges/selectedSieges";
import EventSelectedStands from "../../modals/Stands/selectedStands";
import EventSelectedSocial from "../../modals/Socials/selectedSocials";
import EventSelectedPlaces from "../../modals/Places/selectedPlaces";
import Input from "@mui/material/Input";
import { IconButton } from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import CinemaSelectedSieges from "../CinemaFormFields";

import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";

function FormFields({ formValues }) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.only("sm"));

  const [open, setOpen] = useState(false);
  const [openEventPlaces, setOpenEventPlaces] = useState(false);
  const [openEventSieges, setOpenEventSieges] = useState(false);
  const [openEventStand, setOpenEventStand] = useState(false);
  const [openEventSocial, setOpenEventSocial] = useState(false);

  const [openEventSelectedPlaces, setOpenEventSelectedPlaces] = useState(false);
  const [openEventSelectedSieges, setOpenEventSelectedSieges] = useState(false);
  const [openEventSelectedStand, setOpenEventSelectedStand] = useState(false);
  const [openEventSelectedSocial, setOpenEventSelectedSocial] = useState(false);

  const [categoriesData, setCategoriesData] = useState([]);
  const [placesData, setPlacesData] = useState([]);
  const [standsData, setStandsData] = useState([]);
  const [siegesData, setSiegesData] = useState([]);
  const [socialsData, setSocialsData] = useState([]);
  const [isCinemaMode, setIsCinemaMode] = useState(false);
  const [isCirqueMode, setIsCirqueMode] = useState(false);
  const [showtimes, setShowtimes] = useState([
    {
      generatedId: uuidv4(),
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      eventDate: "",
      eventTime: "",
      eventDoorClosingDate: "",
      eventDoorClosingTime: "",
      prices: [],
    },
  ]);

  // const [selectedCategoriesData, setselectedCategoriesData] = useState([])
  // const [selectedPlacesData, setselectedPlacesData] = useState([])
  // const [selectedStandsData, setselectedStandsData] = useState([])
  // const [selectedSiegesData, setselectedSiegesData] = useState([])

  const [formDataValues, setFormDataValues] = useState({
    eventTitle: formValues?.eventTitle,
    promoVideo: formValues?.promoVideo,
    address: formValues?.address,
    city: formValues?.city || "",
    socialLink: formValues?.socialLink,
    eventPlace: formValues?.eventPlace,
    eventCategory: formValues?.eventCategory,
    eventStand: formValues?.eventStand,
    eventSiege: formValues?.eventSiege,
    startDate: formValues?.startDate,
    startTime: formValues?.startTime,
    endDate: formValues?.endDate,
    endTime: formValues?.endTime,
    eventDate: formValues?.eventDate,
    eventTime: formValues?.eventTime,

    eventDoorClosingDate: formValues?.eventDoorClosingDate,
    eventDoorClosingTime: formValues?.eventDoorClosingTime,

    onlineReservation: true,
    isSelectetivePlaces: false,
    seatIoId: formValues?.seatIoId,
    isSoldOut: false,
  });
  const buildEventDateList = (currentShowtimes, cinemaMode, cirqueMode) => {
    if ((!cinemaMode && !cirqueMode) || !Array.isArray(currentShowtimes)) {
      return [];
    }

    return currentShowtimes.map((showtime) => {
      let processedCloseDoorDate = null;
      if (showtime.eventDoorClosingDate) {
        const [year, month, day] = showtime.eventDoorClosingDate.split("-").map(Number);
        const [hour, minute] = showtime.eventDoorClosingTime
          ? showtime.eventDoorClosingTime.split(":").map(Number)
          : [0, 0];
        const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
        if (!isNaN(dateObjUtc.getTime())) {
          processedCloseDoorDate = dateObjUtc.toISOString();
        } else {
          console.error(
            "DEBUG_DATE_ERROR: Invalid Date created for eventCloseDoorDate with values:",
            showtime.eventDoorClosingDate,
            showtime.eventDoorClosingTime
          );
        }
      }

      let processedStartDate = null;
      if (showtime.startDate) {
        const [year, month, day] = showtime.startDate.split("-").map(Number);
        const [hour, minute] = showtime.startTime
          ? showtime.startTime.split(":").map(Number)
          : [0, 0];
        const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
        if (!isNaN(dateObjUtc.getTime())) {
          processedStartDate = dateObjUtc.toISOString();
        } else {
          console.error(
            "DEBUG_DATE_ERROR: Invalid Date created for startDate with values:",
            showtime.startDate,
            showtime.startTime
          );
        }
      }

      let processedEndDate = null;
      if (showtime.endDate) {
        const [year, month, day] = showtime.endDate.split("-").map(Number);
        const [hour, minute] = showtime.endTime ? showtime.endTime.split(":").map(Number) : [0, 0];
        const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
        if (!isNaN(dateObjUtc.getTime())) {
          processedEndDate = dateObjUtc.toISOString();
        } else {
          console.error(
            "DEBUG_DATE_ERROR: Invalid Date created for endDate with values:",
            showtime.endDate,
            showtime.endTime
          );
        }
      }

      let processedEventDate = null;
      if (showtime.eventDate) {
        const [year, month, day] = showtime.eventDate.split("-").map(Number);
        const [hour, minute] = showtime.eventTime
          ? showtime.eventTime.split(":").map(Number)
          : [0, 0];
        const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
        if (!isNaN(dateObjUtc.getTime())) {
          processedEventDate = dateObjUtc.toISOString();
        } else {
          console.error(
            "DEBUG_DATE_ERROR: Invalid Date created for eventDate with values:",
            showtime.eventDate,
            showtime.eventTime
          );
        }
      }

      return {
        generatedId: showtime.generatedId,
        startDate: processedStartDate,
        endDate: processedEndDate,
        eventDate: processedEventDate,
        eventCloseDoorDate: processedCloseDoorDate,
        prices: showtime.prices,

        isSoldOut: false,
        availableTickets: 0,

        ...(showtime._id && { _id: showtime._id }),
      };
    });
  };

  useEffect(() => {
    getInitialData();
  }, []);

  const getInitialData = async () => {
    try {
      const categories = await getAllCategoriesApi();
      const places = await getAllPlacesApi();
      const stands = await getAllStandsApi();
      // const sieges = await getAllSiegesApi()
      const socials = await getAllSocialsApi();

      setCategoriesData(categories);
      setPlacesData(places);
      setStandsData(stands);
      // setSiegesData(sieges)
      setSocialsData(socials);
    } catch (err) {
      console.log(err);
    }
  };

  const handleClose = (params) => {
    setOpen(false);
  };
  const handleCloseEventPlaces = (params) => {
    setOpenEventPlaces(false);
  };
  const handleCloseEventSieges = (params) => {
    setOpenEventSieges(false);
  };
  const handleCloseEventStands = (params) => {
    setOpenEventStand(false);
  };
  const handleCloseEventSocial = (params) => {
    setOpenEventSocial(false);
  };

  const handleCloseSelectedEventPlaces = (params) => {
    setOpenEventSelectedPlaces(false);
  };
  const handleCloseSelectedEventSieges = (params) => {
    setOpenEventSelectedSieges(false);
  };
  const handleCloseSelectedEventStands = (params) => {
    setOpenEventSelectedStand(false);
  };
  const handleCloseSelectedEventSocial = (params) => {
    setOpenEventSelectedSocial(false);
  };

  const handleAddNewCategoryModal = () => {
    setOpen(true);
  };
  const handleAddNewPlaceModal = () => {
    setOpenEventPlaces(true);
  };
  const handleAddNewSiegeModal = () => {
    setOpenEventSieges(true);
  };
  const handleAddNewResellerModal = () => {
    setOpenEventStand(true);
  };
  const handleAddNewsocialModal = () => {
    setOpenEventSocial(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        [name]: value,
      };

      const eventDateListData = buildEventDateList(showtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,
        startDate: updatedFormData.startDate,
        startTime: updatedFormData.startTime,
        endDate: updatedFormData.endDate,
        endTime: updatedFormData.endTime,
        eventDate: updatedFormData.eventDate,
        eventTime: updatedFormData.eventTime,
        eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
        eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedFormData;
    });
  };

  const handleIsOnlineSwitch = (onlineReservationVal) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        onlineReservation: onlineReservationVal,
      };

      const eventDateListData = buildEventDateList(showtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,

        startDate: updatedFormData.startDate,
        startTime: updatedFormData.startTime,
        endDate: updatedFormData.endDate,
        endTime: updatedFormData.endTime,
        eventDate: updatedFormData.eventDate,
        eventTime: updatedFormData.eventTime,
        eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
        eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedFormData;
    });
  };

  const handleisIsSelectetivePlacesSwitch = (IsSelectetivePlacesValue) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        isSelectetivePlaces: IsSelectetivePlacesValue,
      };

      const eventDateListData = buildEventDateList(showtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,

        startDate: updatedFormData.startDate,
        startTime: updatedFormData.startTime,
        endDate: updatedFormData.endDate,
        endTime: updatedFormData.endTime,
        eventDate: updatedFormData.eventDate,
        eventTime: updatedFormData.eventTime,
        eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
        eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedFormData;
    });
  };

  const handleisIsSoldOutSwitch = (isSoldOut) => {
    const newFormDataValues = {
      ...formDataValues,
      isSoldOut: isSoldOut,
    };

    setFormDataValues(newFormDataValues);
    formValues(newFormDataValues);
  };

  const handleAutocompletePlaceChange = (event, newValue) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventPlace: newValue,
      };

      const eventDateListData = buildEventDateList(showtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,

        startDate: updatedFormData.startDate,
        startTime: updatedFormData.startTime,
        endDate: updatedFormData.endDate,
        endTime: updatedFormData.endTime,
        eventDate: updatedFormData.eventDate,
        eventTime: updatedFormData.eventTime,
        eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
        eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedFormData;
    });
  };
  const handleAutocompleteCategoryChange = (event, newValue) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventCategory: newValue,
      };

      const eventDateListData = buildEventDateList(showtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,
        startDate: updatedFormData.startDate,
        startTime: updatedFormData.startTime,
        endDate: updatedFormData.endDate,
        endTime: updatedFormData.endTime,
        eventDate: updatedFormData.eventDate,
        eventTime: updatedFormData.eventTime,
        eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
        eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedFormData;
    });
  };
  const handleAutocompleteStandChange = (event, newValue) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventStand: newValue,
      };

      const eventDateListData = buildEventDateList(showtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,
        startDate: updatedFormData.startDate,
        startTime: updatedFormData.startTime,
        endDate: updatedFormData.endDate,
        endTime: updatedFormData.endTime,
        eventDate: updatedFormData.eventDate,
        eventTime: updatedFormData.eventTime,
        eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
        eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedFormData;
    });
  };
  const handleAutocompleteSiegeChange = (event, newValue) => {
    setFormDataValues((prevFormData) => {
      const prevSelectedSieges = prevFormData.eventSiege || [];
      const currentSelectedSieges = newValue || [];

      const updatedFormData = {
        ...prevFormData,
        eventSiege: currentSelectedSieges,
      };

      const removedSieges = prevSelectedSieges.filter(
        (prevSiege) =>
          !currentSelectedSieges.some((currentSiege) => currentSiege.id === prevSiege.id)
      );

      setSiegesData((prevSiegesData) => {
        const updatedSiegesData = prevSiegesData.filter(
          (siege) => !removedSieges.some((removed) => removed.id === siege.id)
        );

        setShowtimes((prevShowtimes) => {
          const updatedShowtimes = prevShowtimes.map((showtime) => {
            const updatedPrices = (showtime.prices || []).filter((price) =>
              updatedSiegesData.some((availableSiege) => availableSiege.id === price.id)
            );
            return {
              ...showtime,
              prices: updatedPrices,
            };
          });

          const eventDateListData = buildEventDateList(updatedShowtimes, isCinemaMode, isCirqueMode);

          const parentPayload = {
            ...updatedFormData,
            isCinema: isCinemaMode,
        isCirque: isCirqueMode,
          isCirque: isCirqueMode,
            eventDateList: eventDateListData,
            startDate: updatedFormData.startDate,
            startTime: updatedFormData.startTime,
            endDate: updatedFormData.endDate,
            endTime: updatedFormData.endTime,
            eventDate: updatedFormData.eventDate,
            eventTime: updatedFormData.eventTime,
            eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
            eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
          };
          formValues(parentPayload);

          return updatedShowtimes;
        });

        return updatedSiegesData;
      });

      return updatedFormData;
    });
  };
  const handleAutocompleteSocialChange = (event, newValue) => {
    // Use the updater function to correctly update formDataValues and get prevFormData
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        socialLink: newValue,
      };

      // Construct the complete payload to send to the parent
      // Use the build helper that depends on showtimes and isCinemaMode
      const eventDateListData = buildEventDateList(showtimes, isCinemaMode, isCirqueMode); // Ensure buildEventDateList, showtimes, isCinemaMode are available in scope

      const parentPayload = {
        ...updatedFormData, // Include all fields from the updated formDataValues
        isCinema: isCinemaMode,
        isCirque: isCirqueMode, // Include the current isCinemaMode state
        eventDateList: eventDateListData, // Include the eventDateList derived from showtimes

        // Include individual date/time fields from updatedFormData as requested
        startDate: updatedFormData.startDate,
        startTime: updatedFormData.startTime,
        endDate: updatedFormData.endDate,
        endTime: updatedFormData.endTime,
        eventDate: updatedFormData.eventDate,
        eventTime: updatedFormData.eventTime,
        eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
        eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
      };

      // Send the complete payload to the parent via the formValues prop
      formValues(parentPayload); // Ensure formValues is the prop received by the component // Return the updated local state

      return updatedFormData;
    });
  };

  const handleRefreshData = () => {
    getInitialData();
  };

  const handleNewPlaceData = (data) => {
    setPlacesData((prevData) => [...prevData, data]);
  };
  const handleSelectedNewPlaceData = (data) => {
    const selectedEventPlaces = data.eventPlaces;

    setPlacesData(() => selectedEventPlaces);

    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventPlace: selectedEventPlaces,
      };

      const eventDateListData = buildEventDateList(showtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,
        startDate: updatedFormData.startDate,
        startTime: updatedFormData.startTime,
        endDate: updatedFormData.endDate,
        endTime: updatedFormData.endTime,
        eventDate: updatedFormData.eventDate,
        eventTime: updatedFormData.eventTime,
        eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
        eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedFormData;
    });
  };

  const handleNewStandData = (data) => {
    setStandsData((prevData) => [...prevData, data]);
  };
  const handleSelectedNewStandData = (data) => {
    const selectedEventStands = data.eventStand;

    setStandsData(() => selectedEventStands);

    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventStand: selectedEventStands,
      };

      const eventDateListData = buildEventDateList(showtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,
        startDate: updatedFormData.startDate,
        startTime: updatedFormData.startTime,
        endDate: updatedFormData.endDate,
        endTime: updatedFormData.endTime,
        eventDate: updatedFormData.eventDate,
        eventTime: updatedFormData.eventTime,
        eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
        eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedFormData;
    });
  };

  const handleNewSiegeData = (data) => {
    setSiegesData((prevData) => [...prevData, data]);
  };
  const handleSelectedNewSiegeData = (data) => {
    const selectedEventSiege = data.eventSiege;

    // setSiegesData(() => selectedEventSiege);

    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventSiege: selectedEventSiege,
      };

      const eventDateListData = buildEventDateList(showtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,
        startDate: updatedFormData.startDate,
        startTime: updatedFormData.startTime,
        endDate: updatedFormData.endDate,
        endTime: updatedFormData.endTime,
        eventDate: updatedFormData.eventDate,
        eventTime: updatedFormData.eventTime,
        eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
        eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedFormData;
    });
  };

  const handleNewSocialData = (data) => {
    setSocialsData((prevData) => [...prevData, data]);
  };

  const handleEditePlacesListModal = () => {
    setOpenEventSelectedPlaces(true);
  };
  const handleEditeStandsListModal = () => {
    setOpenEventSelectedStand(true);
  };
  const handleEditeSiegeListModal = () => {
    setOpenEventSelectedSieges(true);
  };
  const handleEditeSocialListModal = () => {
    setOpenEventSelectedSocial(true);
  };
  const handleShowtimeInputChange = (index, event) => {
    const { name, value } = event.target;

    setShowtimes((prevShowtimes) => {
      const updatedShowtimes = [...prevShowtimes];
      updatedShowtimes[index] = { ...updatedShowtimes[index], [name]: value };

      const eventDateListData = buildEventDateList(updatedShowtimes, isCinemaMode, isCirqueMode);

      formValues({
        ...formDataValues,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,

        startDate: formDataValues.startDate,
        startTime: formDataValues.startTime,
        endDate: formDataValues.endDate,
        endTime: formDataValues.endTime,
        eventDate: formDataValues.eventDate,
        eventTime: formDataValues.eventTime,
        eventDoorClosingDate: formDataValues.eventDoorClosingDate,
        eventDoorClosingTime: formDataValues.eventDoorClosingTime,
      });

      return updatedShowtimes;
    });
  };

  const handleRemoveShowtime = (index) => {
    setShowtimes((prevShowtimes) => {
      let updatedShowtimes = prevShowtimes.filter((_, i) => i !== index);
      if ((isCinemaMode || isCirqueMode) && updatedShowtimes.length === 0) {
        updatedShowtimes.push({
          generatedId: uuidv4(),
          startDate: "",
          startTime: "",
          endDate: "",
          endTime: "",
          eventDate: "",
          eventTime: "",
          eventDoorClosingDate: "",
          eventDoorClosingTime: "",
          prices: [],
        });
      }

      const eventDateListData = buildEventDateList(updatedShowtimes, isCinemaMode, isCirqueMode);

      formValues({
        ...formDataValues,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,
        startDate: formDataValues.startDate,
        startTime: formDataValues.startTime,
        endDate: formDataValues.endDate,
        endTime: formDataValues.endTime,
        eventDate: formDataValues.eventDate,
        eventTime: formDataValues.eventTime,
        eventDoorClosingDate: formDataValues.eventDoorClosingDate,
        eventDoorClosingTime: formDataValues.eventDoorClosingTime,
      });

      return updatedShowtimes;
    });
  };

  const handleShowtimePriceChange = (index, selectedPricesArray) => {
    setShowtimes((prevShowtimes) => {
      const updatedShowtimes = [...prevShowtimes];
      const showtimeToUpdate = { ...updatedShowtimes[index] };

      showtimeToUpdate.prices = selectedPricesArray;

      updatedShowtimes[index] = showtimeToUpdate;

      const eventDateListData = buildEventDateList(updatedShowtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...formDataValues,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,
        startDate: formDataValues.startDate,
        startTime: formDataValues.startTime,
        endDate: formDataValues.endDate,
        endTime: formDataValues.endTime,
        eventDate: formDataValues.eventDate,
        eventTime: formDataValues.eventTime,
        eventDoorClosingDate: formDataValues.eventDoorClosingDate,
        eventDoorClosingTime: formDataValues.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedShowtimes;
    });
  };

  const removeSelectedCinemaHour = (index) => {
    try {

    }
    catch (error) {
      console.log(error)
    }
  }

  const handleAddCinemaSiegeData = (siege, timeIndex, showtime) => {
    try {
      siege.map(el => {
        el.fromGeneratedId = showtime.generatedId
      })

    // check if siege is already exist from seleted time id
      const errors = {
        existingSiegeName: false,
        isDuplicated: false,
        emptySiege: false,
        siegeName: null,        
      }

      if(siege.length == 0)
        errors.emptySiege = true

      if(errors.emptySiege) {
        return Swal.fire({
          title: `Ajouter une siége`,
          text: "",
          icon: "error",
        });
      }

      const findDuplicates = (array) => {
        const seen = new Set();
        const duplicates = new Set();
      
        array.forEach((item) => {
          if (seen.has(item)) {
            duplicates.add(item);
          } else {
            seen.add(item);
          }
        });
      
        return [...duplicates];
      };

      const selectedSiegesName = siege.filter(el => el.fromGeneratedId === showtime.generatedId).map(el => el.name)
      if(findDuplicates(selectedSiegesName).length > 0) {
        errors.siegeName = findDuplicates(selectedSiegesName)[0]
        return Swal.fire({
          title: `Ce siège existe déjà (${errors.siegeName})`,
          text: "",
          icon: "error",
        });
      }

      // const addedSiegeName = siegesData.filter(el => el.fromGeneratedId === showtime.generatedId).map(el => el.name)
      // siege.filter(el => el.fromGeneratedId === showtime.generatedId).map(el => {
      //   if(addedSiegeName.includes(el.name)) {
      //     errors.existingSiegeName = true
      //     errors.siegeName = el.name
      //   }
      // })

      // if(errors.existingSiegeName) {
      //   return Swal.fire({
      //     title: `Ce siège existe déjà (${errors.siegeName})`,
      //     text: "",
      //     icon: "error",
      //   });
      // }

      const selectedSiegeId = siege[0].fromGeneratedId

      // Immutable update — create new array with new objects so React detects the change
      setShowtimes((prevShowTimes) => {
        const updatedShowtimes = prevShowTimes.map(el =>
          el.generatedId === selectedSiegeId ? { ...el, prices: siege } : el
        )

        // Build processed ISO dates from the updated showtimes
        const eventDateListData = buildEventDateList(updatedShowtimes, isCinemaMode, isCirqueMode)

        // Update the siegesData array
        setSiegesData((prevSiegesData) => {
          const updatedPrevData = prevSiegesData.filter(el => el.fromGeneratedId !== showtime.generatedId)
          const updatedSiegesData = [...updatedPrevData, siege].flat(1)

          setFormDataValues((prevFormData) => {
            const updatedFormData = {
              ...prevFormData,
              isCinema: isCinemaMode,
        isCirque: isCirqueMode,
              isCirque: isCirqueMode,
              eventDateList: eventDateListData,
              eventSiege: updatedSiegesData,
            };

            formValues(updatedFormData);
            return updatedFormData;
          });

          return updatedSiegesData;
        })

        return updatedShowtimes
      })

      // console.log("showtimes: ", showtimes)
      // console.log("siegesData: ", siegesData)
      // console.log("formDataValues: ", formDataValues)

      return Swal.fire({
        title: "Modifiée",
        text: "",
        icon: "success",
      });
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <Card>
      <EventCategories
        openEventCategory={open}
        closeEventCategory={handleClose}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={null}
      />
      <EventPlaces
        openEventPlace={openEventPlaces}
        closeEventPlace={handleCloseEventPlaces}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={null}
        newPlaceData={handleNewPlaceData}
      />
      <EventStands
        openEventStand={openEventStand}
        closeEventStand={handleCloseEventStands}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={null}
        newStandData={handleNewStandData}
      />
      <EventSieges
        openEventSiege={openEventSieges}
        closeEventSiege={handleCloseEventSieges}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={null}
        newSiegeData={handleNewSiegeData}
      />
      <EventSocial
        openEventSocial={openEventSocial}
        closeEventSocial={handleCloseEventSocial}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={null}
        newSocialData={handleNewSocialData}
      />

      <EventSelectedPlaces
        openEventSelectedPlace={openEventSelectedPlaces}
        closeEventSelectedPlace={handleCloseSelectedEventPlaces}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={formDataValues.eventPlace}
        newPlaceData={handleSelectedNewPlaceData}
      />
      <EventSelectedStands
        openEventSelectedStand={openEventSelectedStand}
        closeEventSelectedStand={handleCloseSelectedEventStands}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={formDataValues.eventStand}
        newStandData={handleSelectedNewStandData}
      />
      <EventSelectedSieges
        openEventSelectedSiege={openEventSelectedSieges}
        closeEventSelectedSiege={handleCloseSelectedEventSieges}
        isEdite={false}
        openModalItemData={siegesData}
        newSiegeData={setSiegesData}
      />
      <EventSelectedSocial
        openEventSelectedSocial={openEventSelectedSocial}
        closeEventSelectedSocial={handleCloseSelectedEventSocial}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={formDataValues.socialLink}
        newSocialLinkData={handleNewPlaceData}
      />

      <MDBox p={3}>
        <MDBox mb={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="city-select-label">Ville</InputLabel>
                <Select
                  labelId="city-select-label"
                  name="city"
                  value={formDataValues.city || ""}
                  onChange={handleInputChange}
                  label="Ville"
                >
                  <MenuItem value=""><em>— Sélectionner une ville —</em></MenuItem>
                  {cities.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </MDBox>
        <MDTypography variant="h5">Informations sur l'événement</MDTypography>
        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField
                type="text"
                label="Titre de l'événement"
                name="eventTitle"
                value={formDataValues.eventTitle}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField
                type="text"
                label="Vidéo promotionnelle"
                name="promoVideo"
                value={formDataValues.promoVideo}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField
                type="text"
                label="Adresse"
                name="address"
                value={formDataValues.address}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container xs={12}>
            <Grid item xs={12} md={6}>
              <MDBox>
                <MDBox display="inline-block">
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    textTransform="capitalize"
                  >
                    Catégories
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  multiple
                  defaultValue={[]}
                  options={categoriesData}
                  getOptionLabel={(option) => option.name}
                  onChange={handleAutocompleteCategoryChange}
                  renderInput={(params) => (
                    <FormField {...params} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 0, md: 5 } }}>
              <MDButton color="success" variant="text" onClick={handleAddNewCategoryModal}>
                Ajouter une nouvelle catégorie
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container xs={12}>
            <Grid item xs={12} md={6}>
              <MDBox>
                <MDBox display="inline-block">
                  <Icon
                    fontSize="medium"
                    color="success"
                    style={{
                      cursor: "pointer",
                      position: "relative",
                      top: "5px",
                      marginRight: "10px",
                    }}
                    onClick={handleEditePlacesListModal}
                  >
                    edit
                  </Icon>
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    textTransform="capitalize"
                  >
                    Lieu de l'événement :
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  multiple
                  defaultValue={[]}
                  options={placesData}
                  getOptionLabel={(option) => option.name}
                  onChange={handleAutocompletePlaceChange}
                  renderInput={(params) => (
                    <FormField {...params} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 0, md: 5 } }}>
              <MDButton color="success" variant="text" onClick={handleAddNewPlaceModal}>
                Ajouter un nouveau lieu
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container xs={12}>
            <Grid item xs={12} md={6}>
              <MDBox>
                <MDBox display="inline-block">
                  <Icon
                    fontSize="medium"
                    color="success"
                    style={{
                      cursor: "pointer",
                      position: "relative",
                      top: "5px",
                      marginRight: "10px",
                    }}
                    onClick={handleEditeStandsListModal}
                  >
                    edit
                  </Icon>
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    textTransform="capitalize"
                  >
                    Stands
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  multiple
                  defaultValue={[]}
                  options={standsData}
                  getOptionLabel={(option) => option.name}
                  onChange={handleAutocompleteStandChange}
                  renderInput={(params) => (
                    <FormField {...params} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 0, md: 5 } }}>
              <MDButton color="success" variant="text" onClick={handleAddNewResellerModal}>
                Ajouter un nouveau stand
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
        {
          (!isCinemaMode && !isCirqueMode) ?
          <MDBox mt={1}>
            <Grid container xs={12}>
              <Grid item xs={12} md={6}>
                <MDBox>
                  <MDBox display="inline-block">
                    <Icon
                      fontSize="medium"
                      color="success"
                      style={{
                        cursor: "pointer",
                        position: "relative",
                        top: "5px",
                        marginRight: "10px",
                      }}
                      onClick={handleEditeSiegeListModal}
                    >
                      edit
                    </Icon>
                    <MDTypography
                      component="label"
                      variant="button"
                      fontWeight="regular"
                      color="text"
                      textTransform="capitalize"
                    >
                      Siège (VIP / Normal ...)
                    </MDTypography>
                  </MDBox>
                  <Autocomplete
                    multiple
                    // defaultValue={[]}
                    value={formDataValues.eventSiege || []}
                    options={siegesData}
                    getOptionLabel={(option) => option.name}
                    onChange={handleAutocompleteSiegeChange}
                    renderInput={(params) => (
                      <FormField {...params} InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} sx={{ mt: { xs: 0, md: 5 } }}>
                <MDButton color="success" variant="text" onClick={handleAddNewSiegeModal}>
                  Ajouter un nouveau siège
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
            :
          <></>
        }
        <MDBox mt={1}>
          <Grid container xs={12}>
            <Grid item xs={12} md={6}>
              <MDBox>
                <MDBox display="inline-block">
                  {/* <Icon fontSize="medium" color="success" style={{ cursor: 'pointer', position: 'relative', top: '5px', marginRight: '10px' }} onClick={handleEditeSocialListModal}>edit</Icon> */}
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    textTransform="capitalize"
                  >
                    Réseaux sociaux
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  multiple
                  defaultValue={[]}
                  options={socialsData}
                  getOptionLabel={(option) => option.name}
                  onChange={handleAutocompleteSocialChange}
                  renderInput={(params) => (
                    <FormField {...params} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 0, md: 5 } }}>
              <MDButton color="success" variant="text" onClick={handleAddNewsocialModal}>
                Ajouter
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <FormControlLabel
            control={
              <Switch
                checked={formDataValues.onlineReservation}
                onChange={() => handleIsOnlineSwitch(!formDataValues.onlineReservation)}
              />
            }
            label="Résérvation online"
          />
        </MDBox>

        {/* <MDBox mt={1}>
          <Grid container spacing={3} xs={12}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formDataValues.isSoldOut}
                    onChange={() => handleisIsSoldOutSwitch(!formDataValues.isSoldOut)}
                  />
                }
                label="Soldout"
              />
            </Grid>
          </Grid>
        </MDBox> */}

        <MDBox mt={1}>
          <Grid container spacing={3} xs={12}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formDataValues.isSelectetivePlaces}
                    onChange={() =>
                      handleisIsSelectetivePlacesSwitch(!formDataValues.isSelectetivePlaces)
                    }
                  />
                }
                label="Réservation via le plan"
              />
              <MDBox mt={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isCinemaMode}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setIsCinemaMode(newValue);
                        if (newValue) setIsCirqueMode(false);

                        if (newValue) {
                          setSiegesData([])
                          const newFormDataValues = {
                            ...formDataValues,
                            eventSiege: [],
                          };
                          setFormDataValues(newFormDataValues)

                          setShowtimes([
                            {
                              generatedId: uuidv4(),
                              startDate: "",
                              startTime: "",
                              endDate: "",
                              endTime: "",
                              eventDate: "",
                              eventTime: "",
                              eventDoorClosingDate: "",
                              eventDoorClosingTime: "",
                            },
                          ]);
                        }

                        const eventDateListData = newValue
                          ? showtimes.map((showtime) => ({
                              startDate: showtime.startDate
                                ? new Date(
                                    showtime.startDate + "T" + (showtime.startTime || "00:00")
                                  )
                                : null,
                              endDate: showtime.endDate
                                ? new Date(showtime.endDate + "T" + (showtime.endTime || "00:00"))
                                : null,
                              eventDate: showtime.eventDate
                                ? new Date(
                                    showtime.eventDate + "T" + (showtime.eventTime || "00:00")
                                  )
                                : null,
                              eventCloseDoorDate: showtime.eventDoorClosingDate
                                ? new Date(
                                    showtime.eventDoorClosingDate +
                                      "T" +
                                      (showtime.eventDoorClosingTime || "00:00")
                                  )
                                : null,
                            }))
                          :
                            [];
                        formValues({
                          ...formDataValues,
                          isCinema: newValue,
                          isCirque: false,
                          eventDateList: eventDateListData,
                        });
                      }}
                    />
                  }
                  label="Mode Cinéma"
                />
              </MDBox>
              <MDBox mt={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isCirqueMode}
                      disabled={isCinemaMode}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setIsCirqueMode(newValue);

                        if (newValue) {
                          setSiegesData([]);
                          setFormDataValues({ ...formDataValues, eventSiege: [] });
                          setShowtimes([
                            {
                              generatedId: uuidv4(),
                              startDate: "",
                              startTime: "",
                              endDate: "",
                              endTime: "",
                              eventDate: "",
                              eventTime: "",
                              eventDoorClosingDate: "",
                              eventDoorClosingTime: "",
                            },
                          ]);
                        }

                        const eventDateListData = newValue
                          ? showtimes.map((showtime) => ({
                              startDate: showtime.startDate
                                ? new Date(showtime.startDate + "T" + (showtime.startTime || "00:00"))
                                : null,
                              endDate: showtime.endDate
                                ? new Date(showtime.endDate + "T" + (showtime.endTime || "00:00"))
                                : null,
                              eventDate: showtime.eventDate
                                ? new Date(showtime.eventDate + "T" + (showtime.eventTime || "00:00"))
                                : null,
                              eventCloseDoorDate: showtime.eventDoorClosingDate
                                ? new Date(
                                    showtime.eventDoorClosingDate +
                                      "T" +
                                      (showtime.eventDoorClosingTime || "00:00")
                                  )
                                : null,
                            }))
                          : [];
                        formValues({
                          ...formDataValues,
                          isCirque: newValue,
                          isCinema: false,
                          eventDateList: eventDateListData,
                        });
                      }}
                    />
                  }
                  label="Mode Cirque"
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}></Grid>
            {formDataValues.isSelectetivePlaces && (
              <Grid item xs={12} md={6}>
                <FormField
                  type="text"
                  label="Seat.io id"
                  name="seatIoId"
                  value={formDataValues.seatIoId}
                  onChange={handleInputChange}
                />
              </Grid>
            )}
          </Grid>
        </MDBox>
        {(isCinemaMode || isCirqueMode) ? (
          <Grid item xs={12}>
            <MDBox mt={3}>
              {showtimes.map((showtime, index) => (
                <MDBox
                  key={index}
                  mb={2}
                  sx={{ border: "1px solid #ccc", padding: 2, borderRadius: 1 }}
                >
                  <MDTypography variant="h6">Séance {index + 1}</MDTypography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Date de début
                        </MDTypography>
                        <Input
                          type="date"
                          name="startDate"
                          value={showtime.startDate}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Heure de début
                        </MDTypography>
                        <Input
                          type="time"
                          name="startTime"
                          value={showtime.startTime}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Date de fin
                        </MDTypography>
                        <Input
                          type="date"
                          name="endDate"
                          value={showtime.endDate}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Heure de fin
                        </MDTypography>
                        <Input
                          type="time"
                          name="endTime"
                          value={showtime.endTime}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Date de l'événement
                        </MDTypography>
                        <Input
                          type="date"
                          name="eventDate"
                          value={showtime.eventDate}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Heure de l'événement
                        </MDTypography>
                        <Input
                          type="time"
                          name="eventTime"
                          value={showtime.eventTime}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Date de fermeture des portes
                        </MDTypography>
                        <Input
                          type="date"
                          name="eventDoorClosingDate"
                          value={showtime.eventDoorClosingDate}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Heure de fermeture des portes
                        </MDTypography>
                        <Input
                          type="time"
                          name="eventDoorClosingTime"
                          value={showtime.eventDoorClosingTime}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <MDBox mb={1}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Siège (VIP / Normal ...)
                        </MDTypography>
                        {/* <Autocomplete
                          multiple
                          options={formDataValues.eventSiege || []}
                          getOptionLabel={(option) => option.name}
                          value={
                            siegesData.filter((siege) =>
                              showtime.prices?.some(
                                (selectedPrice) => selectedPrice.id === siege.id
                              )
                            ) || []
                          }
                          onChange={(event, newValue) => handleShowtimePriceChange(index, newValue)}
                          renderInput={(params) => (
                            <MDInput
                              {...params}
                              label="Siège (VIP / Normal ...)"
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        /> */}
                        <CinemaSelectedSieges
                          isEdite={false}
                          openModalItemData={[]}
                          // openModalItemData={siegesData}
                          newSiegeData={(siege, timeIndex) => handleAddCinemaSiegeData(siege, timeIndex, showtime)}
                          // newSiegeData={setSiegesData}
                          selectedTimeIndex={index}
                        />
                      </MDBox>
                    </Grid>
                    {showtimes.length > 1 && (
                      <MDBox
                        mt={1}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "flex-center",
                          marginLeft: "460px",
                          marginBottom: "-80px",
                        }}
                      >
                        <Icon
                          color="error"
                          fontSize="medium"
                          onClick={() => handleRemoveShowtime(index)}
                        >
                          delete
                        </Icon>
                      </MDBox>
                    )}
                  </Grid>
                  {(isCinemaMode || isCirqueMode) && (
                    <MDBox
                      mt={1}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "flex-center",
                        marginLeft: "465px",
                      }}
                    >
                      <Icon
                        color="success"
                        fontSize="medium"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setShowtimes([
                            ...showtimes,
                            {
                              generatedId: uuidv4(),
                              startDate: "",
                              startTime: "",
                              endDate: "",
                              endTime: "",
                              eventDate: "",
                              eventTime: "",
                              eventDoorClosingDate: "",
                              eventDoorClosingTime: "",
                            },
                          ])
                        }
                      >
                        add
                      </Icon>
                    </MDBox>
                  )}
                </MDBox>
              ))}
            </MDBox>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Date de début
                </MDTypography>
                <Input
                  type="date"
                  name="startDate"
                  value={formDataValues.startDate}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Heure de début
                </MDTypography>
                <Input
                  type="time"
                  name="startTime"
                  value={formDataValues.startTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Date de fin
                </MDTypography>
                <Input
                  type="date"
                  name="endDate"
                  value={formDataValues.endDate}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Heure de fin
                </MDTypography>
                <Input
                  type="time"
                  name="endTime"
                  value={formDataValues.endTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Date de l'événement
                </MDTypography>
                <Input
                  type="date"
                  name="eventDate"
                  value={formDataValues.eventDate}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Heure de l'événement
                </MDTypography>
                <Input
                  type="time"
                  name="eventTime"
                  value={formDataValues.eventTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Date de fermeture des portes
                </MDTypography>
                <Input
                  type="date"
                  name="eventDoorClosingDate"
                  value={formDataValues.eventDoorClosingDate}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Heure de fermeture des portes
                </MDTypography>
                <Input
                  type="time"
                  name="eventDoorClosingTime"
                  value={formDataValues.eventDoorClosingTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
          </Grid>
        )}
      </MDBox>
    </Card>
  );
}

FormFields.propTypes = {
  formValues: PropTypes.string.isRequired,
};

export default FormFields;
